import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalApi, getCustToken, setCustToken, API_BASE } from '../../api';

export default function PortalLogin() {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [wxState, setWxState] = useState<'none' | 'logging' | 'needBind'>('none');
  const [wxOpenid, setWxOpenid] = useState('');
  const [bindCode, setBindCode] = useState('');
  const [bindPwd, setBindPwd] = useState('');
  const [bindErr, setBindErr] = useState('');
  const nav = useNavigate();

  if (getCustToken()) { nav('/portal', { replace: true }); return null; }

  // 微信登录：自动读 URL ?wxcode= 并登录/绑定（仅在微信内由小程序 web-view 注入 code）
  useEffect(() => {
    if (getCustToken()) return;
    const wxcode = new URLSearchParams(location.search).get('wxcode');
    if (!wxcode) return;
    setWxState('logging');
    (async () => {
      try {
        const r: any = await fetch(`${API_BASE}/api/wechat/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: wxcode, role: 'customer' }),
        }).then((res) => res.json());
        if (r.code !== 0) throw new Error(r.msg || '微信登录失败');
        if (r.data.bound) {
          setCustToken(r.data.token);
          nav('/portal');
        } else {
          setWxOpenid(r.data.openid);
          setWxState('needBind');
        }
      } catch (e: any) {
        setErr(e.message || '微信登录失败，请用账号密码登录');
        setWxState('none');
      }
    })();
  }, []);

  const submit = async (e: any) => {
    e.preventDefault();
    setErr('');
    try {
      const d = await portalApi('/login', { method: 'POST', body: JSON.stringify({ code, password }) });
      setCustToken(d.token);
      nav('/portal');
    } catch (e: any) { setErr(e.message); }
  };

  const doBind = async (e: any) => {
    e.preventDefault();
    setBindErr('');
    try {
      const r: any = await fetch(`${API_BASE}/api/wechat/bind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ openid: wxOpenid, role: 'customer', account: bindCode, password: bindPwd }),
      }).then((res) => res.json());
      if (r.code !== 0) throw new Error(r.msg || '绑定失败');
      setCustToken(r.data.token);
      nav('/portal');
    } catch (e: any) { setBindErr(e.message); }
  };

  const wxcodePresent = new URLSearchParams(location.search).get('wxcode');

  if (wxState === 'logging') {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-emerald-700 to-emerald-900 px-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-sm text-center space-y-3">
          <div className="text-3xl">💬</div>
          <h1 className="text-lg font-bold">微信登录中…</h1>
          <p className="text-xs text-slate-400">正在用微信身份登录您的客户门户</p>
        </div>
      </div>
    );
  }

  if (wxState === 'needBind') {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-emerald-700 to-emerald-900 px-4">
        <form onSubmit={doBind} className="bg-white p-7 rounded-2xl shadow-xl w-full max-w-sm space-y-5">
          <div className="text-center">
            <div className="text-3xl mb-1">🔗</div>
            <h1 className="text-xl font-bold">绑定客户账号</h1>
            <p className="text-xs text-slate-400 mt-1">微信已验证，请输入您的客户编码与密码完成绑定</p>
          </div>
          <input className="w-full border border-slate-200 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="客户编码（如 C001）" value={bindCode} onChange={(e) => setBindCode(e.target.value)} />
          <input type="password" className="w-full border border-slate-200 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="专属密码" value={bindPwd} onChange={(e) => setBindPwd(e.target.value)} />
          {bindErr && <div className="text-red-500 text-sm text-center">{bindErr}</div>}
          <button className="w-full bg-emerald-600 text-white rounded-lg py-3 text-base font-bold hover:bg-emerald-700 active:scale-95 transition">绑定并登录</button>
          <div className="text-xs text-slate-400 text-center">还没有账号？联系荣牧科技开通</div>
        </form>
      </div>
    );
  }

  return (
    <div className="h-full flex items-center justify-center bg-gradient-to-br from-emerald-700 to-emerald-900 px-4">
      <form onSubmit={submit} className="bg-white p-7 rounded-2xl shadow-xl w-full max-w-sm space-y-5">
        <div className="text-center">
          <div className="text-3xl mb-1">🛒</div>
          <h1 className="text-xl font-bold">客户自助报货门户</h1>
          <p className="text-xs text-slate-400 mt-1">在线下单 · 实时查库存 · 手机电脑通用</p>
        </div>
        {wxcodePresent && wxState === 'none' && (
          <div className="text-xs text-center text-amber-600 bg-amber-50 border border-amber-200 rounded-lg py-2">微信自动登录未完成，请用账号密码登录</div>
        )}
        <input className="w-full border border-slate-200 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="客户编码（如 C001）" value={code} onChange={(e) => setCode(e.target.value)} />
        <input type="password" className="w-full border border-slate-200 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="专属密码" value={password} onChange={(e) => setPassword(e.target.value)} />
        {err && <div className="text-red-500 text-sm text-center">{err}</div>}
        <button className="w-full bg-emerald-600 text-white rounded-lg py-3 text-base font-bold hover:bg-emerald-700 active:scale-95 transition">登 录</button>
        <div className="text-xs text-slate-400 text-center">
          账号由荣牧科技开通后方可使用，如无法登录请联系我们。
          <br />
          <a href="/portal/activate" className="text-emerald-600 underline font-medium">🎟️ 我有激活码，立即开通</a>
          <br />
          <a href="/login" className="text-emerald-600 underline">内部员工？返回管理后台</a>
        </div>
      </form>
    </div>
  );
}
